// Orchestrator — Dashboard hooks.
//
// React Query hooks for the orchestrator. The chat stream
// itself is driven by @ai-sdk/react's useChat — these hooks cover the
// session/messages CRUD that wraps it.

import { useMutation, useQuery } from "@tanstack/react-query";
import type { UIMessage } from "ai";
import { useAuth } from "@/lib/auth";
import { useAgentIdentity, withAgentAuth } from "@/lib/agent-identity";
import { apiFetch, post, del } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";
import type { ContextBreakdown } from "@/pages/orchestrator/ContextMeter";

export type OrchestratorMode = "default" | "plan" | "connector";

export type OrchestratorTodoPhase = "use_case" | "connector" | "integration" | "widget";

export interface OrchestratorTodo {
  id: string;
  label: string;
  status: "pending" | "in_progress" | "completed" | "blocked";
  phase: OrchestratorTodoPhase;
  note?: string;
  /** Deep link to the produced asset (in-app path) — rendered on the build rail. */
  link?: { label: string; href: string };
}

// Mirrors planDiagramSchema in services/agent .../tools/modes.ts — the
// business-level flow the agent passes to exit_plan_mode, rendered by the
// plan review card.
export type OrchestratorPlanNodeType = "system" | "connector" | "workflow" | "trigger" | "step" | "user";

export interface OrchestratorPlanDiagram {
  title?: string;
  nodes: Array<{
    id: string;
    type: OrchestratorPlanNodeType;
    label: string;
    description?: string;
  }>;
  edges: Array<{
    from: string;
    to: string;
    label?: string;
  }>;
}

export interface OrchestratorSession {
  threadId: string;
  orgId: string;
  userId: string;
  title: string | null;
  status: string;
  mode: OrchestratorMode;
  autoAccept: boolean;
  approvalAllowlist: string[];
  todos: OrchestratorTodo[];
  // Input tokens the model read on the last provider call of the previous turn
  // — what the composer's context meter shows before this session streams again.
  lastInputTokens: number;
  // How that token count split across the prompt's blocks on the same call —
  // seeds the breakdown panel so reopening a thread does not show it empty.
  // Null on threads that predate the column or have never run a turn.
  lastContextBreakdown: ContextBreakdown | null;
  // Context window of the orchestrator's model. A server-side constant stamped
  // onto every row rather than a sibling field, so the hook keeps returning a
  // plain array and the optimistic cache writes below stay unchanged.
  contextWindow: number;
  // Persisted messages on the thread. Populated by the sessions list only — the
  // rail states it against each row; 0 anywhere a single session was fetched.
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Org + request-init for every orchestrator call.
 *
 * An AgentIdentity override (the builder's live preview, acting as the selected
 * customer) supplies both the bearer token and the org the cache keys hang off,
 * so a preview's sessions neither read nor pollute the signed-in user's. With
 * no override this is exactly the previous behaviour: the signed-in user's org
 * and whatever Authorization apiFetch applies by default.
 */
function useAgentScope(): { orgId?: string; init: (i?: RequestInit) => RequestInit | undefined } {
  const { userContext } = useAuth();
  const override = useAgentIdentity();
  return {
    orgId: override.orgId ?? userContext?.orgId,
    init: (i?: RequestInit) => withAgentAuth(i, override.token),
  };
}

export function useOrchestratorSessions() {
  const { orgId, init } = useAgentScope();
  return useQuery({
    queryKey: keys.orchestrator.sessions(orgId),
    queryFn: () =>
      apiFetch<{ sessions: OrchestratorSession[] }>(`${API_BASE}/api/v1/agent/orchestrator/sessions`, init()).then(
        (r) => r.sessions,
      ),
    enabled: !!orgId,
    // Sessions list refreshes on every `useChat` onFinish to pick up the
    // backend-derived title; we don't need a stale window on top of that.
    staleTime: 0,
  });
}

export function useOrchestratorMessages(threadId: string | undefined) {
  const { orgId, init } = useAgentScope();
  return useQuery({
    queryKey: keys.orchestrator.messages(orgId, threadId),
    queryFn: () =>
      apiFetch<{ messages: UIMessage[] }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}/messages`,
        init(),
      ).then((r) => r.messages),
    enabled: !!orgId && !!threadId,
    // Infinity for a real transcript - it only changes through this app, and
    // every writer already invalidates. But NEVER for an empty one.
    //
    // An empty array is almost always a snapshot taken before the server had
    // finished writing, not a genuinely empty thread. The client's onFinish
    // fires when the SSE stream closes and invalidates immediately; the
    // backend's own onFinish saveMessages can land after that, so the refetch
    // reads a thread whose turn is not persisted yet and caches []. Worse, it
    // can resolve AFTER the unmount cleanup removeQueries() that exists to
    // catch exactly this - repopulating the cache with [] once nothing is left
    // to correct it. Reopening the thread then rendered a blank chat with no
    // request in flight, and only a page reload cleared it.
    //
    // Keeping [] stale costs one request on the genuinely-empty case (a thread
    // whose first turn has not run) and removes the whole class.
    staleTime: (q) => (q.state.data?.length ? Infinity : 0),
  });
}

// Single-file read from the thread's virtual filesystem — the plan review
// card fetches the /plan-<slug>.md the agent authored before exit_plan_mode.
// staleTime 0 so a revised plan re-fetches on each re-presentation; retry
// covers the sub-second race between the streamed approval part and the
// backend's onFinish file flush.
export function useOrchestratorFile(threadId: string | undefined, path: string | undefined) {
  const { orgId, init } = useAgentScope();
  return useQuery({
    queryKey: keys.orchestrator.file(orgId, threadId, path),
    queryFn: () =>
      apiFetch<{ path: string; content: string }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}/files?path=${encodeURIComponent(path!)}`,
        init(),
      ).then((r) => r.content),
    enabled: !!orgId && !!threadId && !!path,
    staleTime: 0,
    retry: 2,
  });
}

export function useCreateOrchestratorSession() {
  const { orgId, init } = useAgentScope();
  return useMutation({
    mutationFn: (body: { title?: string }) =>
      apiFetch<{ session: OrchestratorSession }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions`,
        init(post(body ?? {})),
      ).then((r) => r.session),
    onSuccess: (newSession) => {
      // Prepend the new session into the cache synchronously so any consumer
      // that re-renders before the background refetch lands (the page-level
      // guard that bounces unknown threadIds back to /orchestrator) sees
      // it immediately. Without this the navigation to the new thread would
      // race the stale sessions list and flicker back to the blank panel.
      queryClient.setQueryData<OrchestratorSession[]>(
        keys.orchestrator.sessions(orgId),
        (prev) => (prev ? [newSession, ...prev] : [newSession]),
      );
      queryClient.invalidateQueries({ queryKey: keys.orchestrator.sessions(orgId) });
    },
  });
}

// Explicit Stop. Calls POST /sessions/:threadId/stop so the backend aborts
// the in-flight run deterministically rather than relying on the fetch abort
// from useChat.stop() propagating through TCP close (which races with the
// AI-SDK stream pipeline and with rapid Stop+resend flows).
export function useStopOrchestratorRun() {
  const { init } = useAgentScope();
  return useMutation({
    mutationFn: (threadId: string) =>
      apiFetch<{ ok: true; wasRunning: boolean }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}/stop`,
        init(post({})),
      ),
  });
}

// Hand a running turn a freshly-minted access token.
//
// The agent reaches the platform with the caller's OWN token, captured once
// when /stream was posted. Keycloak access tokens live 5 minutes and an
// autonomous run can outlast that many times over, at which point every
// platform tool call the agent makes fails as an expired credential. There is
// no body — apiFetch goes through authFetch, which refreshes the token before
// sending, so the Authorization header on this request IS the fresh token.
export function useRefreshOrchestratorToken() {
  const { init } = useAgentScope();
  return useMutation({
    mutationFn: (threadId: string) =>
      apiFetch<{ ok: true }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}/token`,
        init(post({})),
      ),
  });
}

// Mid-flight steering. Posts a user message to /inject — the supervisor loop
// picks it up at the next step boundary and feeds it into the next streamText
// iteration as a user turn (Claude-Code-style "queue while running"). If no
// run is active, the backend returns { queued: false } so the FE can fall
// back to a normal /stream send.
export function useInjectOrchestratorMessage() {
  const { init } = useAgentScope();
  return useMutation({
    mutationFn: ({ threadId, message }: { threadId: string; message: UIMessage }) =>
      apiFetch<{ queued: boolean; depth?: number }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}/inject`,
        init(post({ message })),
      ),
  });
}

// Toggle Claude-Code-style auto-accept for the session's gated mutating tools.
// Optimistically patches the cached session so the header switch flips at once;
// the sessions list refetch (onChat finish) reconciles the canonical value.
export function useSetOrchestratorAutoAccept() {
  const { orgId, init } = useAgentScope();
  return useMutation({
    mutationFn: ({ threadId, enabled }: { threadId: string; enabled: boolean }) =>
      apiFetch<{ ok: true; autoAccept: boolean }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}/auto-accept`,
        init(post({ enabled })),
      ),
    onMutate: ({ threadId, enabled }) => {
      queryClient.setQueryData<OrchestratorSession[]>(
        keys.orchestrator.sessions(orgId),
        (prev) =>
          prev?.map((s) => (s.threadId === threadId ? { ...s, autoAccept: enabled } : s)),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.orchestrator.sessions(orgId) });
    },
  });
}

// "Always allow this tool for the session" from an approval card.
export function useAllowOrchestratorTool() {
  const { orgId, init } = useAgentScope();
  return useMutation({
    mutationFn: ({ threadId, toolName }: { threadId: string; toolName: string }) =>
      apiFetch<{ ok: true; toolName: string }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}/allow-tool`,
        init(post({ toolName })),
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.orchestrator.sessions(orgId) });
    },
  });
}

// Rename a session. Optimistic so the inline edit commits instantly; the
// sessions refetch reconciles.
export function useRenameOrchestratorSession() {
  const { orgId, init } = useAgentScope();
  return useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      apiFetch<{ ok: true; title: string }>(
        `${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}`,
        init({ method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title }) }),
      ),
    onMutate: ({ threadId, title }) => {
      queryClient.setQueryData<OrchestratorSession[]>(
        keys.orchestrator.sessions(orgId),
        (prev) => prev?.map((s) => (s.threadId === threadId ? { ...s, title } : s)),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.orchestrator.sessions(orgId) });
    },
  });
}

export function useDeleteOrchestratorSession() {
  const { orgId, init } = useAgentScope();
  return useMutation({
    mutationFn: (threadId: string) =>
      apiFetch(`${API_BASE}/api/v1/agent/orchestrator/sessions/${threadId}`, init(del)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.orchestrator.sessions(orgId) });
    },
  });
}
